import { Avatar, ResolvedUser } from "./Avatar";

type UserDisplayVariant = "stacked" | "inline" | "avatarOnly";

interface UserDisplayProps {
  user?: ResolvedUser;
  fallbackLabel?: string;
  variant: UserDisplayVariant;
  size?: "sm" | "md";
}

export function UserDisplay({
  user,
  fallbackLabel,
  variant,
  size,
}: UserDisplayProps) {
  if (variant === "avatarOnly") {
    return (
      <Avatar user={user} fallbackLabel={fallbackLabel} size={size ?? "md"} />
    );
  }

  if (variant === "inline") {
    const name = user
      ? [user.first_name, user.last_name].filter(Boolean).join(" ")
      : "";
    const label = name || user?.email || (fallbackLabel ?? "");
    // A name alone is ambiguous on an audit trail — someone who set only a
    // first name is indistinguishable from the next Ada. The email always
    // identifies exactly one person, so it rides along whenever the label is
    // a name. When the label already is the email there is nothing to add,
    // and a fallbackLabel has no user behind it to take an email from.
    const qualifier = name ? user?.email : undefined;
    // align-middle centers the avatar on the text line; without it the
    // inline-flex aligns to the baseline and hangs below it. Callers should
    // give the surrounding prose a line-height at least as tall as the avatar
    // so wrapped lines stay evenly spaced.
    return (
      <span className="inline-flex items-center gap-2 align-middle">
        <Avatar user={user} fallbackLabel={fallbackLabel} size={size ?? "sm"} />
        <span className="truncate">
          {label}
          {qualifier && (
            // font-normal because ActivityLogList wraps this in font-bold to
            // make the actor stand out in a sentence; the email is context,
            // not emphasis.
            <span className="text-textSubtle ml-1 font-normal">
              ({qualifier})
            </span>
          )}
        </span>
      </span>
    );
  }

  // variant === "stacked"
  const hasName = user && (user.first_name || user.last_name);

  return (
    <div className="flex items-center gap-2">
      <Avatar user={user} fallbackLabel={fallbackLabel} size={size ?? "md"} />
      <div className="min-w-0 flex-1">
        {hasName ? (
          <>
            <div className="text-textDefault truncate font-semibold">
              {[user!.first_name, user!.last_name].filter(Boolean).join(" ")}
            </div>
            <div className="text-textSubtle truncate text-sm">
              {user!.email}
            </div>
          </>
        ) : (
          <div className="text-textDefault truncate">
            {user ? user.email : fallbackLabel}
          </div>
        )}
      </div>
    </div>
  );
}
